import { Input } from 'src/design/atoms/Input';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Checkbox } from 'src/design/atoms/Checkbox';
import React from 'react';
import { JobUserSelectV2 } from 'src/app/selectors/JobUserSelectV2';
import { RoleUserSelector } from './RoleUserSelector';
import type { WorkflowStepDto } from './EditDrawer';

export interface IEditDrawerNotificationsTabProps {
  current: WorkflowStepDto;
  isTemplateEditor: boolean;
  onFieldChange: (field: string, value: any) => void;
}

/** @registry Notifications tab of a workflow step's EditDrawer — assignee notification toggle and roles/users to additionally notify; on review steps, the edit-after-signing alert and its recipients. */
export const EditDrawerNotificationsTab: React.FC<
  IEditDrawerNotificationsTabProps
> = ({ current, isTemplateEditor, onFieldChange }) => {
  const isReviewStep =
    current.isEmployeeReviewStep || current.isSupervisorReviewStep;

  const showRecipients = !isReviewStep || !!current.notifyOnSignedEdit;

  return (
    <Stack gap="lg">
      <Title order={3}>Notification Settings</Title>
      {isReviewStep ? (
        <Checkbox
          checked={current.notifyOnSignedEdit ?? false}
          label="Notify on edits after signing"
          description="Email the signer and the recipients below when an admin edits the record after this step is signed."
          onChange={(e) =>
            onFieldChange('notifyOnSignedEdit', e.currentTarget.checked)
          }
        />
      ) : (
        <Checkbox
          checked={current.notifyAssignees}
          label="Notify Assigned Users"
          onChange={(e) =>
            onFieldChange('notifyAssignees', e.currentTarget.checked)
          }
        />
      )}

      {showRecipients && (
        <>
          <Title order={3}>Additional Notifications</Title>

          <Input.Wrapper
            label="Additional Roles to Notify"
            description={
              isReviewStep
                ? 'Users with these roles also receive the edit-after-signing email'
                : 'Users with these roles will be notified when transitioned to this status'
            }
          >
            <RoleUserSelector
              onUserSelect={(val) =>
                onFieldChange(
                  'excludedNotifyUsers',
                  val.map((user) => ({ jobUser: user, jobUserId: user.id })),
                )
              }
              initialUsers={current.excludedNotifyUsers.map(
                (user) => user.jobUser,
              )}
              field={'notifyRoles'}
              placeholder="Select roles to notify..."
              initialSelectedRoles={current.notifyRoles.map(
                (role) => role.organizationRole,
              )}
              selectedRoles={current.notifyRoles.map(
                (role) => role.organizationRole,
              )}
              selectDescription="Select users to EXCLUDE from notify roles"
              onFieldChange={(field, value) =>
                onFieldChange(
                  field,
                  value.map((role) => ({
                    organizationRoleId: role.id,
                    organizationRole: role,
                  })),
                )
              }
            />
          </Input.Wrapper>

          <Input.Wrapper
            hidden={isTemplateEditor}
            label="Additional Users to Notify"
            description={
              isReviewStep
                ? 'Selected users also receive the edit-after-signing email, regardless of roles'
                : 'Selected users will be notified when transitioned to this status regardless of roles'
            }
          >
            <JobUserSelectV2
              hidden={isTemplateEditor}
              onChange={(value) =>
                onFieldChange(
                  'notifyUsers',
                  value.map((user) => ({ jobUserId: user.id, jobUser: user })),
                )
              }
              value={current.notifyUsers.map((user) => user.jobUser)}
              hideLabel
            />
          </Input.Wrapper>
        </>
      )}
    </Stack>
  );
};
